import React from "react";
import PropTypes from "prop-types";

const shapes = {
  square: "rounded-[0px]",
};
const variants = {
  underline: {
    gray_900: "text-white-a700",
    gray_900_01: "text-white-a700",
  },
  pixelArt: {
    default: "btn-pixel-art",
  },
  pixelAction: {
    default: "btn-pixel-action",
  },
};
const sizes = {
  "4xl": "h-[62px] pr-1 text-[26px]",
  xl: "h-[40px] text-[17px]",
  md: "text-[16px]",
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  className?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  shape?: keyof typeof shapes;
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  color?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  className = "",
  leftIcon,
  rightIcon,
  shape,
  variant = "underline",
  size = "4xl",
  color = "",
  ...restProps
}) => {
  // Special case for pixel art button
  if (variant === "pixelArt") {
    return (
      <div className="btn-pixel-outer">
        <div className="btn-pixel-border"></div>
        <button
          className={`${className} btn-pixel-art flex flex-row items-center justify-center text-center cursor-pointer whitespace-nowrap text-white-a700 font-upheavalttbrk font-upheaval ${shape && shapes[shape]} ${size && sizes[size]}`}
          {...restProps}
        >
          {!!leftIcon && <div className="flex items-center mr-2">{leftIcon}</div>}
          {children}
          {!!rightIcon && <div className="flex items-center ml-2">{rightIcon}</div>}
        </button>
      </div>
    );
  }

  // Special case for pixel action buttons (START and LEARN MORE)
  if (variant === "pixelAction") {
    return (
      <div className="btn-action-outer">
        <button
          className={`${className} btn-pixel-action flex w-full flex-row items-center justify-center text-center cursor-pointer whitespace-nowrap text-white-a700 font-upheavalttbrk font-upheaval ${shape && shapes[shape]} ${size && sizes[size]}`}
          {...restProps}
        >
          {!!leftIcon && <div className="flex items-center mr-2">{leftIcon}</div>}
          {children}
          {!!rightIcon && <div className="flex items-center ml-2">{rightIcon}</div>}
        </button>
        <div className="pixelated-outline"></div>
      </div>
    );
  }

  return (
    <button
      className={`${className} flex flex-row items-center justify-center text-center cursor-pointer whitespace-nowrap text-white-a700 font-upheavalttbrk font-upheaval ${shape && shapes[shape]} ${size && sizes[size]} ${variant && color && variants[variant]?.[color as keyof typeof variants[typeof variant]] || ""}`}
      {...restProps}
    >
      {!!leftIcon && leftIcon}
      {children}
      {!!rightIcon && rightIcon}
    </button>
  );
};

Button.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  shape: PropTypes.oneOf(["square"]),
  size: PropTypes.oneOf(["4xl", "xl", "md"]),
  variant: PropTypes.oneOf(["underline", "pixelArt", "pixelAction"]),
  color: PropTypes.oneOf(["gray_900", "gray_900_01", "default"]),
};

export default Button;
